import type {BuiltZoomOption, CustomZoomPresets, DisplayConfig, UIElements, ZoomOptionParams, ZoomType} from "@/modules/pdf/types";

const getZoomTypes: Record<string, ZoomType> = {
    auto: 'auto',
    number: 'number',
    fitPageHeight: 'page-fit',
    fitPageWidth: 'page-width',
}

const getDefaultDisplayConfig: Required<DisplayConfig> = {
    minZoom: 0.25,
    maxZoom: 5,
    zoomStep: 0.5,
    devicePixelRatio: window.devicePixelRatio || 1,
    thumbnailWidth: 170
}

const toZoomType = (raw: unknown): ZoomType => {

    const canBeNumber = Number(raw);
    if (Number.isFinite(canBeNumber) && canBeNumber > 0) return canBeNumber;

    const value = typeof raw === 'string' ? raw : String(raw)

    return (
        value === 'auto' ? getZoomTypes.auto :
            value === 'page-width' ? getZoomTypes.fitPageWidth :
                value === 'page-fit' ? getZoomTypes.fitPageHeight :
                    value === 'number' ? getZoomTypes.number :
                        getZoomTypes.auto
    )
}

const isNumericZoomType = (mode: ZoomType): boolean => {
    return typeof mode === 'number' || mode === 'number';
}

const buildZoomOptions = ({minZoom, maxZoom, zoomStep}: ZoomOptionParams): BuiltZoomOption[] => {

    if (zoomStep <= 0) throw new Error('zoomStep must be greater than 0');
    if (minZoom > maxZoom) throw new Error('minZoom cannot be greater than maxZoom');

    const options: BuiltZoomOption[] = [];
    // avoid floating point drift by rounding to 4 decimals
    const round = (n: number): number => Math.round(n * 10_000) / 10_000;

    for (let zoom = minZoom; zoom <= maxZoom; zoom = round(zoom + zoomStep)) {
        options.push({
            key: round(zoom),
            label: `${Math.round(zoom * 100)}%`,
        });
    }

    return options;
};

const getUIElements = (): UIElements => {
    const main = document.getElementById('reader__engine');
    const nameContainer = main.querySelector<HTMLElement>('[data-controller="name"]');
    const sidebarContainer = main.querySelector<HTMLElement>('[data-container="sidebar"]');

    return {
        engine: main,
        containers: {
            main: main.querySelector<HTMLElement>('[data-container="main"]'),
            readingZone: main.querySelector<HTMLElement>('[data-container="reading"]'),
            pages: main.querySelector<HTMLElement>('[data-container="pages"]'),
        },

        name: {
            container: nameContainer,
            renderers: nameContainer.querySelectorAll<HTMLElement>('.name__wrapper div')
        },

        toolbar: {
            container: main.querySelector<HTMLElement>('[data-container="toolbar"]'),
            downloadCta: main.querySelectorAll<HTMLElement>('[data-download]'),
            infosCta: main.querySelectorAll<HTMLElement>('[data-infos]'),
            themeToggler: main.querySelectorAll<HTMLElement>('[data-theme="toggler"]'),
            homeCta: main.querySelectorAll<HTMLElement>('[data-controller="home"]'),
        },

        sidebar: {
            container: sidebarContainer,
            scrollable: sidebarContainer.querySelector<HTMLElement>('.scrollbable'),
            toggler: main.querySelectorAll<HTMLElement>('[data-sidebar="toggler"]'),
        },

        search: {
            input: {
                container: main.querySelector<HTMLInputElement>('[data-search="container"]'),
                toggler: main.querySelectorAll<HTMLElement>('[data-search="toggler"]'),
                input: main.querySelector<HTMLInputElement>('[data-search="input"]')
            },
            options: {

                container: main.querySelector<HTMLElement>('[data-search="options"]'),
                status: main.querySelector<HTMLElement>('[data-search="status"]'),
                prev: main.querySelector<HTMLElement>('[data-search="prev"]'),
                next: main.querySelector<HTMLElement>('[data-search="next"]'),
            },
        },

        zoom: {
            picker: {
                input: main.querySelector<HTMLSelectElement>('[data-zoom="picker"]'),
                wrapper: main.querySelector<HTMLSelectElement>('[data-zoom="wrapper"]'),
                display: main.querySelector<HTMLSelectElement>('[data-zoom="display"]'),
            },
            out: main.querySelector<HTMLElement>('[data-zoom="out"]'),
            in: main.querySelector<HTMLElement>('[data-zoom="in"]'),
        },

        paginator: {
            container: main.querySelector<HTMLElement>('[data-container="paginator"]'),
            input: main.querySelectorAll<HTMLInputElement>('[data-paginator="input"]'),
            current: main.querySelectorAll<HTMLElement>('[data-paginator="current"]'),
            total: main.querySelectorAll<HTMLElement>('[data-paginator="total"]'),
            first: main.querySelectorAll<HTMLElement>('[data-paginator="first"]'),
            prev: main.querySelectorAll<HTMLElement>('[data-paginator="prev"]'),
            next: main.querySelectorAll<HTMLElement>('[data-paginator="next"]'),
            last: main.querySelectorAll<HTMLElement>('[data-paginator="last"]'),
        }
    }
};


const staticOptions: BuiltZoomOption[] = [
    {key: 'auto', label: 'Automatique', displayLabel: 'Auto', selected: true},
    {key: 'page-width', label: 'Ajuster à la largeur de la page', displayLabel: 'Largeur de page'},
    {key: 'page-fit', label: 'Ajuster à la hauteur de la page', displayLabel: 'Hauteur de page'}
];

export const ReaderUtils = {
    DefaultDisplayConfig: getDefaultDisplayConfig,

    getUIElements,

    zoom: {
        options: {
            static: {
                presets: staticOptions,
                custom: ({auto, fitPageHeight, fitPageWidth}: CustomZoomPresets): BuiltZoomOption[] => {
                    const customized: BuiltZoomOption[] = [];
                    const __default: BuiltZoomOption[] = [...staticOptions];

                    for (let i = 0; i < __default.length; i++) {
                        const {key, label, displayLabel, selected} = __default[i]

                        if (key === 'auto') customized.push({
                            key,
                            label: auto?.label || label,
                            displayLabel: auto?.displayLabel || displayLabel,
                            selected: auto?.selected || selected
                        });

                        if (key === 'page-fit') customized.push({
                            key,
                            label: fitPageHeight?.label || label,
                            displayLabel: fitPageHeight?.displayLabel || displayLabel,
                            selected: fitPageHeight?.selected || selected
                        });

                        if (key === 'page-width') customized.push({
                            key,
                            label: fitPageWidth?.label || label,
                            displayLabel: fitPageWidth?.displayLabel || displayLabel,
                            selected: fitPageWidth?.selected || selected
                        });
                    }

                    return customized;
                },
            },

            types: getZoomTypes,
        },

        build: buildZoomOptions,
        cast: toZoomType,
        isNumeric: isNumericZoomType,
    },
}
export default ReaderUtils;